<template>
  <div class="tv-channels-container">
    <!-- Header & Filters -->
    <vx-card class="mb-4">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <!-- Left: Title & Add Button -->
        <div class="flex items-center gap-4">
          <h4 class="font-semibold text-lg">TV Channels</h4>
          <vs-button color="success" icon="add" size="small" @click="openModal(null)">
            Add Channel
          </vs-button>
        </div>

        <!-- Right: Filters & View Toggle -->
        <div class="flex flex-wrap items-center gap-3">
          <vs-input 
            v-model="filters.search" 
            placeholder="Search..." 
            icon="search"
            size="small"
            class="w-48"
          />
          <vs-select v-model="filters.package_id" size="small" class="w-40">
            <vs-select-item :value="null" text="All Packages" />
            <vs-select-item v-for="pack in packages" :key="pack.id" :value="pack.id" :text="pack.name" />
          </vs-select>
          <vs-select v-model="filters.status" size="small" class="w-28">
            <vs-select-item :value="null" text="All Status" />
            <vs-select-item :value="1" text="Active" />
            <vs-select-item :value="0" text="Inactive" />
          </vs-select>
          <vs-select v-model="filters.featured" size="small" class="w-28">
            <vs-select-item :value="null" text="All" />
            <vs-select-item :value="1" text="Featured" />
          </vs-select>
          <vs-select v-model="filters.kids" size="small" class="w-28">
            <vs-select-item :value="null" text="All" />
            <vs-select-item :value="1" text="Kids" />
          </vs-select>
          <div class="view-toggle flex border rounded overflow-hidden">
            <button :class="['px-3 py-1', viewMode === 'table' ? 'bg-success text-white' : 'bg-gray-100']" @click="viewMode = 'table'">
              <feather-icon icon="ListIcon" svgClasses="w-4 h-4" />
            </button>
            <button :class="['px-3 py-1', viewMode === 'grid' ? 'bg-success text-white' : 'bg-gray-100']" @click="viewMode = 'grid'">
              <feather-icon icon="GridIcon" svgClasses="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- Active Filters -->
      <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
        <span class="text-sm text-gray-500">Filters:</span>
        <vs-chip v-if="filters.search" closable @click="filters.search = ''">
          Search: {{ filters.search }}
        </vs-chip>
        <vs-chip v-if="filters.package_id" closable @click="filters.package_id = null" color="primary">
          {{ getPackageName(filters.package_id) }}
        </vs-chip>
        <vs-chip v-if="filters.status !== null" closable @click="filters.status = null" :color="filters.status ? 'success' : 'danger'">
          {{ filters.status ? 'Active' : 'Inactive' }}
        </vs-chip>
        <vs-chip v-if="filters.featured" closable @click="filters.featured = null" color="warning">
          Featured
        </vs-chip>
        <vs-chip v-if="filters.kids" closable @click="filters.kids = null" color="primary">
          Kids
        </vs-chip>
        <vs-button type="flat" color="danger" size="small" @click="clearFilters">Clear All</vs-button>
      </div>
    </vx-card>

    <!-- Add/Edit Dialog -->
    <vs-popup class="channel-dialog" :title="ch.id ? 'Edit Channel' : 'Add New Channel'" :active.sync="dialogActive">
      <div class="dialog-content">
        <!-- Package Selection -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-1">Package <span class="text-danger">*</span></label>
          <vs-select v-model="ch.package_id" class="w-full">
            <vs-select-item v-for="pack in packages" :key="pack.id" :value="pack.id" :text="pack.name" />
          </vs-select>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium mb-1">Channel Name <span class="text-danger">*</span></label>
            <vs-input v-model="ch.name" placeholder="Enter channel name" class="w-full" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Arabic Name</label>
            <vs-input v-model="ch.ar_name" placeholder="أدخل اسم القناة" class="w-full" dir="rtl" />
          </div>
        </div>

        <div class="mt-4">
          <label class="block text-sm font-medium mb-1">Stream URL</label>
          <vs-input v-model="ch.stream_link" placeholder="https://..." class="w-full" />
        </div>

        <div class="grid grid-cols-4 gap-3 mt-4">
          <div class="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span class="text-xs font-medium">Active</span>
            <vs-switch v-model="ch.status" color="success" />
          </div>
          <div class="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span class="text-xs font-medium">Featured</span>
            <vs-switch v-model="ch.featured" color="warning" />
          </div>
          <div class="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span class="text-xs font-medium">Kids</span>
            <vs-switch v-model="ch.kids" color="primary" />
          </div>
          <div class="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span class="text-xs font-medium">Editor</span>
            <vs-switch v-model="ch.editor_choice" color="danger" />
          </div>
        </div>

        <div class="grid grid-cols-3 gap-4 mt-4">
          <div>
            <label class="block text-sm font-medium mb-1">Logo</label>
            <div class="upload-box small" @click="$refs.logo.click()">
              <img v-if="ch.logo && typeof ch.logo === 'string'" :src="ch.logo" class="upload-preview" />
              <img v-else-if="logoPreview" :src="logoPreview" class="upload-preview" />
              <div v-else class="upload-placeholder">
                <feather-icon icon="UploadIcon" svgClasses="w-5 h-5 text-gray-400" />
              </div>
            </div>
            <input type="file" ref="logo" class="hidden" accept="image/*" @change="handleLogo()" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Image</label>
            <div class="upload-box small" @click="$refs.chImage.click()">
              <img v-if="ch.image && typeof ch.image === 'string'" :src="ch.image" class="upload-preview" />
              <img v-else-if="imagePreview" :src="imagePreview" class="upload-preview" />
              <div v-else class="upload-placeholder">
                <feather-icon icon="UploadIcon" svgClasses="w-5 h-5 text-gray-400" />
              </div>
            </div>
            <input type="file" ref="chImage" class="hidden" accept="image/*" @change="handleChImage()" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Secondary</label>
            <div class="upload-box small" @click="$refs.chSecImage.click()">
              <img v-if="ch.secondary_image && typeof ch.secondary_image === 'string'" :src="ch.secondary_image" class="upload-preview" />
              <img v-else-if="secImagePreview" :src="secImagePreview" class="upload-preview" />
              <div v-else class="upload-placeholder">
                <feather-icon icon="UploadIcon" svgClasses="w-5 h-5 text-gray-400" />
              </div>
            </div>
            <input type="file" ref="chSecImage" class="hidden" accept="image/*" @change="handleChSecImage()" />
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6 pt-4 border-t">
          <vs-button type="flat" @click="dialogActive = false">Cancel</vs-button>
          <vs-button color="success" @click="saveChannel" :disabled="!ch.package_id || !ch.name">
            {{ ch.id ? 'Update' : 'Create' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Preview Dialog -->
    <vs-popup class="preview-dialog" title="Channel Preview" :active.sync="previewActive">
      <div class="preview-content">
        <div v-if="previewChannel" class="mb-4">
          <h4 class="font-semibold">{{ previewChannel.name }}</h4>
          <p class="text-sm text-gray-500">{{ previewChannel.ar_name }}</p>
        </div>
        <video v-if="previewUrl" ref="previewVideo" :src="previewUrl" controls autoplay class="w-full rounded"></video>
        <div v-else class="text-center py-10">
          <feather-icon icon="VideoOffIcon" svgClasses="w-16 h-16 text-gray-300 mx-auto" />
          <p class="text-gray-500 mt-4">No stream available</p>
        </div>
      </div>
    </vs-popup>

    <!-- Bulk Action Bar -->
    <div v-if="selectedIds.length > 0" class="bulk-action-bar">
      <span class="bulk-count">{{ selectedIds.length }} selected</span>
      <vs-select v-model="bulkAction" size="small" class="w-36">
        <vs-select-item value="status" text="Status" />
        <vs-select-item value="featured" text="Featured" />
        <vs-select-item value="editor_choice" text="Editor Choice" />
        <vs-select-item value="kids" text="Kids" />
      </vs-select>
      <vs-select v-model="bulkValue" size="small" class="w-24">
        <vs-select-item :value="1" text="ON" />
        <vs-select-item :value="0" text="OFF" />
      </vs-select>
      <vs-button color="success" size="small" :disabled="bulkLoading" @click="applyBulkAction">
        <span v-if="bulkLoading" class="inline-spinner" style="width:14px;height:14px;border-width:2px"></span>
        <span v-else>Apply</span>
      </vs-button>
      <vs-button type="flat" color="dark" size="small" @click="selectedIds = []">Cancel</vs-button>
    </div>

    <!-- Table View -->
    <vx-card v-if="viewMode === 'table'" class="p-0" style="position:relative">
      <div v-if="tableLoading" class="table-loading-overlay">
        <div class="loading-spinner"></div>
      </div>
      <div class="table-responsive">
        <table class="drag-table">
          <thead>
            <tr>
              <th style="width:36px">
                <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" class="bulk-checkbox" />
              </th>
              <th style="width:50px">#</th>
              <th style="width:60px">Image</th>
              <th>Name</th>
              <th>Package</th>
              <th style="width:90px">Status</th>
              <th style="width:130px">Badges</th>
              <th style="width:170px">Actions</th>
            </tr>
          </thead>
          <draggable
            v-model="channels"
            tag="tbody"
            handle=".drag-handle"
            animation="200"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            drag-class="drag-dragging"
            @end="updateSequence"
          >
            <tr v-for="(channel, index) in channels" :key="channel.id" :class="{ 'row-inactive': !channel.status, 'row-loading': loadingIds.indexOf(channel.id) > -1, 'row-selected': selectedIds.indexOf(channel.id) > -1 }">
              <td>
                <input type="checkbox" :checked="selectedIds.indexOf(channel.id) > -1" @change="toggleSelect(channel.id)" class="bulk-checkbox" />
              </td>
              <td>
                <div class="flex items-center gap-2">
                  <span class="drag-handle">
                    <feather-icon icon="MenuIcon" svgClasses="w-4 h-4 text-gray-400" />
                  </span>
                  <span class="text-sm text-gray-500">{{ (pagination.currentPage - 1) * pagination.perPage + index + 1 }}</span>
                </div>
              </td>
              <td>
                <img v-if="channel.image || channel.logo" :src="channel.image || channel.logo" class="w-10 h-7 object-cover rounded" />
                <div v-else class="w-10 h-7 bg-gray-100 rounded flex items-center justify-center">
                  <feather-icon icon="TvIcon" svgClasses="w-4 h-4 text-gray-300" />
                </div>
              </td>
              <td>
                <p class="font-medium text-sm">{{ channel.name }}</p>
                <p class="text-xs text-gray-500" dir="rtl">{{ channel.ar_name }}</p>
              </td>
              <td>
                <vs-chip color="primary" size="small">{{ getPackageName(channel.package_id) }}</vs-chip>
              </td>
              <td>
                <div v-if="loadingIds.indexOf(channel.id) > -1" class="inline-spinner"></div>
                <vs-switch v-else :value="!!channel.status" color="success" @input="toggleStatus(channel, $event)" />
              </td>
              <td>
                <div class="flex gap-1">
                  <vs-chip v-if="channel.featured" color="warning" size="small">★</vs-chip>
                  <vs-chip v-if="channel.kids" color="primary" size="small">Kids</vs-chip>
                  <vs-chip v-if="channel.editor_choice" color="danger" size="small">EC</vs-chip>
                </div>
              </td>
              <td>
                <div class="flex gap-1">
                  <vs-button type="flat" color="primary" icon="play_arrow" size="small" @click="openPreview(channel)" />
                  <vs-button type="flat" color="warning" icon="edit" size="small" @click="openModal(channel)" />
                  <vs-button type="flat" color="danger" icon="delete" size="small" @click="confirmDelete(channel)" />
                </div>
              </td>
            </tr>
          </draggable>
        </table>
      </div>

      <!-- Server-side Pagination -->
      <div v-if="pagination.lastPage > 1" class="flex items-center justify-between mt-4 px-2">
        <span class="text-sm text-gray-500">
          Showing {{ (pagination.currentPage - 1) * pagination.perPage + 1 }}–{{ Math.min(pagination.currentPage * pagination.perPage, pagination.total) }} of {{ pagination.total }}
        </span>
        <div class="flex items-center gap-1">
          <vs-button type="flat" :disabled="pagination.currentPage === 1" icon="chevron_left" size="small" @click="goToPage(pagination.currentPage - 1)" />
          <vs-button
            v-for="p in pagination.lastPage"
            :key="p"
            :type="p === pagination.currentPage ? 'filled' : 'flat'"
            :color="p === pagination.currentPage ? 'success' : 'dark'"
            size="small"
            @click="goToPage(p)"
          >{{ p }}</vs-button>
          <vs-button type="flat" :disabled="pagination.currentPage === pagination.lastPage" icon="chevron_right" size="small" @click="goToPage(pagination.currentPage + 1)" />
        </div>
      </div>
    </vx-card>

    <!-- Grid View -->
    <div v-else>
      <draggable v-model="filteredChannels" handle=".grid-drag-handle" @end="updateSequence" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        <vx-card v-for="channel in filteredChannels" :key="channel.id" class="channel-card p-0" :class="{ 'opacity-50': !channel.status }">
          <div class="grid-drag-handle absolute top-2 left-2 bg-white rounded p-1 shadow cursor-move opacity-0 z-10">
            <feather-icon icon="MoveIcon" svgClasses="w-3 h-3 text-gray-400" />
          </div>
          
          <div class="status-toggle absolute top-2 right-2 z-10" @click="loadingIds.indexOf(channel.id) === -1 && toggleStatus(channel, !channel.status)">
            <div v-if="loadingIds.indexOf(channel.id) > -1" class="inline-spinner" style="width:16px;height:16px;border-width:2px"></div>
            <feather-icon v-else :icon="channel.status ? 'EyeIcon' : 'EyeOffIcon'" :svgClasses="'w-4 h-4 ' + (channel.status ? 'text-success' : 'text-danger')" />
          </div>

          <div class="relative h-24 overflow-hidden bg-gray-100 cursor-pointer" @click="openPreview(channel)">
            <img v-if="channel.image || channel.logo" :src="channel.image || channel.logo" class="w-full h-full object-cover" />
            <div v-else class="w-full h-full flex items-center justify-center">
              <feather-icon icon="TvIcon" svgClasses="w-8 h-8 text-gray-300" />
            </div>
            <div class="play-overlay absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0">
              <feather-icon icon="PlayIcon" svgClasses="w-8 h-8 text-white" />
            </div>
          </div>

          <div class="p-2">
            <h6 class="font-medium text-sm truncate">{{ channel.name }}</h6>
            <p class="text-xs text-gray-400 truncate">{{ getPackageName(channel.package_id) }}</p>
            <div class="flex items-center gap-1 mt-1">
              <span v-if="channel.featured" class="badge featured">★</span>
              <span v-if="channel.kids" class="badge kids">K</span>
              <span v-if="channel.editor_choice" class="badge editor">E</span>
            </div>
          </div>

          <div class="flex border-t">
            <button class="flex-1 py-1 text-warning hover:bg-warning hover:bg-opacity-10" @click="openModal(channel)">
              <feather-icon icon="EditIcon" svgClasses="w-4 h-4" />
            </button>
            <button class="flex-1 py-1 text-danger hover:bg-danger hover:bg-opacity-10 border-l" @click="confirmDelete(channel)">
              <feather-icon icon="TrashIcon" svgClasses="w-4 h-4" />
            </button>
          </div>
        </vx-card>
      </draggable>
    </div>

    <!-- Empty State -->
    <vx-card v-if="filteredChannels.length === 0" class="text-center py-10">
      <feather-icon icon="TvIcon" svgClasses="w-16 h-16 text-gray-300 mx-auto" />
      <h4 class="text-lg text-gray-500 mt-4">No channels found</h4>
      <p class="text-gray-400 mb-4">{{ hasActiveFilters ? 'Try adjusting your filters' : 'Add your first channel' }}</p>
      <vs-button v-if="hasActiveFilters" color="primary" @click="clearFilters">Clear Filters</vs-button>
      <vs-button v-else color="success" @click="openModal(null)">Add Channel</vs-button>
    </vx-card>
  </div>
</template>

<script>
/* eslint-disable */
import draggable from "vuedraggable";

export default {
  components: { draggable },

  data() {
    return {
      dialogActive: false,
      previewActive: false,
      previewUrl: '',
      previewChannel: null,
      viewMode: 'table',
      packages: [],
      channels: [],
      tableLoading: false,
      loadingIds: [],
      selectedIds: [],
      bulkLoading: false,
      bulkAction: 'status',
      bulkValue: 1,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20
      },
      logoPreview: null,
      imagePreview: null,
      secImagePreview: null,
      filters: {
        search: '',
        package_id: null,
        status: null,
        featured: null,
        kids: null
      },
      ch: {
        id: 0,
        package_id: null,
        featured: false,
        kids: false,
        editor_choice: false,
        status: true,
        name: "",
        ar_name: "",
        stream_link: '',
        image: "",
        secondary_image: "",
        logo: "",
      },
    };
  },

  created() {
    if (this.$route && this.$route.query && this.$route.query.package) {
      this.filters.package_id = parseInt(this.$route.query.package);
    }
    this.fetchData();
  },

  computed: {
    filteredChannels() {
      return this.channels;
    },

    allSelected() {
      return this.channels.length > 0 && this.selectedIds.length === this.channels.length;
    },

    hasActiveFilters() {
      return this.filters.search ||
             this.filters.package_id !== null ||
             this.filters.status !== null ||
             this.filters.featured !== null ||
             this.filters.kids !== null;
    }
  },

  watch: {
    filters: {
      handler() {
        this.pagination.currentPage = 1;
        this.fetchData();
      },
      deep: true
    }
  },

  methods: {
    async fetchData() {
      this.tableLoading = true;
      try {
        await this.$store.dispatch("Tv/fetchPackages", { page: 1 });
        this.packages = this.$store.state.Tv.packages || [];

        const params = new URLSearchParams();
        params.set('page', this.pagination.currentPage);
        if (this.filters.search) params.append('q', this.filters.search);
        if (this.filters.package_id !== null) params.append('package_id', this.filters.package_id);
        if (this.filters.status !== null) params.append('status', this.filters.status);
        if (this.filters.featured !== null) params.append('featured', this.filters.featured);
        if (this.filters.kids !== null) params.append('kids', this.filters.kids);

        const res = await this.axios.get(`${this.$apiUrl}/v2/tv/admin/channels?` + params.toString());
        const body = res.data;
        this.pagination.lastPage = body.last_page || 1;
        this.pagination.total = body.total || 0;
        this.pagination.perPage = body.per_page || 20;
        this.channels = (body.data || []).sort((a, b) => (a.sequence != null ? a.sequence : 9999) - (b.sequence != null ? b.sequence : 9999));
      } catch (err) {
        console.error('Error fetching data:', err);
      }
      this.tableLoading = false;
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.pagination.currentPage = page;
      this.fetchData();
    },

    getPackageName(packageId) {
      const pkg = this.packages.find(p => p.id === packageId);
      return pkg ? pkg.name : 'Unknown';
    },

    clearFilters() {
      this.filters = {
        search: '',
        package_id: null,
        status: null,
        featured: null,
        kids: null
      };
    },

    openModal(channel) {
      this.logoPreview = null;
      this.imagePreview = null;
      this.secImagePreview = null;

      if (channel) {
        this.ch = { ...channel };
      } else {
        this.ch = {
          id: 0,
          package_id: this.filters.package_id || (this.packages.length > 0 ? this.packages[0].id : null),
          featured: false,
          kids: false,
          editor_choice: false,
          status: true,
          name: "",
          ar_name: "",
          stream_link: '',
          image: "",
          secondary_image: "",
          logo: "",
        };
      }
      this.dialogActive = true;
    },

    openPreview(channel) {
      this.previewChannel = channel;
      this.previewUrl = channel.stream_link;
      this.previewActive = true;
    },

    async saveChannel() {
      if (!this.ch.package_id || !this.ch.name) {
        this.$vs.notify({ color: 'warning', title: 'Warning', text: 'Please fill required fields' });
        return;
      }

      this.$vs.loading();
      const data = new FormData();
      data.append('id', this.ch.id);
      data.append('name', this.ch.name);
      data.append('ar_name', this.ch.ar_name || '');
      data.append('stream_link', this.ch.stream_link || '');
      data.append('featured', this.ch.featured ? 1 : 0);
      data.append('kids', this.ch.kids ? 1 : 0);
      data.append('editor_choice', this.ch.editor_choice ? 1 : 0);
      data.append('status', this.ch.status ? 1 : 0);
      data.append('package_id', this.ch.package_id);

      if (this.ch.logo && typeof this.ch.logo !== 'string') {
        data.append('logo', this.ch.logo);
      }
      if (this.ch.image && typeof this.ch.image !== 'string') {
        data.append('image', this.ch.image);
      }
      if (this.ch.secondary_image && typeof this.ch.secondary_image !== 'string') {
        data.append('secondary_image', this.ch.secondary_image);
      }

      try {
        await this.axios.post(`${this.$apiUrl}/v2/tv/channels`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        this.$vs.loading.close();
        this.$vs.notify({ color: 'success', title: 'Channel Saved', text: '' });
        this.dialogActive = false;
        this.fetchData();
      } catch (err) {
        this.$vs.loading.close();
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Failed to save channel' });
      }
    },

    async toggleStatus(channel, newValue) {
      const newStatus = newValue ? 1 : 0;
      const oldStatus = channel.status;
      channel.status = newValue;
      this.loadingIds.push(channel.id);
      try {
        const data = new FormData();
        data.append('id', channel.id);
        data.append('name', channel.name);
        data.append('ar_name', channel.ar_name || '');
        data.append('stream_link', channel.stream_link || '');
        data.append('package_id', channel.package_id);
        data.append('featured', channel.featured ? 1 : 0);
        data.append('kids', channel.kids ? 1 : 0);
        data.append('editor_choice', channel.editor_choice ? 1 : 0);
        data.append('status', newStatus);
        await this.axios.post(`${this.$apiUrl}/v2/tv/channels`, data);
        this.$vs.notify({ 
          color: newStatus ? 'success' : 'warning', 
          title: newStatus ? 'Channel Activated' : 'Channel Deactivated',
          text: '' 
        });
      } catch (err) {
        channel.status = oldStatus;
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Failed to update status' });
      }
      this.loadingIds = this.loadingIds.filter(function(id) { return id !== channel.id; });
    },

    toggleSelect(id) {
      var idx = this.selectedIds.indexOf(id);
      if (idx > -1) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(id);
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIds = [];
      } else {
        this.selectedIds = this.channels.map(function(c) { return c.id; });
      }
    },

    async applyBulkAction() {
      if (!this.selectedIds.length) return;
      this.bulkLoading = true;
      try {
        const data = new FormData();
        data.append('action', this.bulkAction);
        data.append('value', this.bulkValue);
        this.selectedIds.forEach(function(id) { data.append('ids[]', id); });
        await this.axios.post(`${this.$apiUrl}/v2/tv/channels/bluck_action`, data);
        // Update local state optimistically
        var field = this.bulkAction;
        var val = this.bulkValue;
        var ids = this.selectedIds;
        this.channels.forEach(function(c) {
          if (ids.indexOf(c.id) > -1) c[field] = val;
        });
        this.$vs.notify({ color: 'success', title: 'Bulk Update', text: 'Channels updated successfully' });
        this.selectedIds = [];
      } catch (err) {
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Bulk action failed' });
      }
      this.bulkLoading = false;
    },

    async updateSequence(evt) {
      const ids = this.filteredChannels.map(c => c.id);
      if (!ids.length) return;
      var movedId = (evt && this.channels[evt.newIndex]) ? this.channels[evt.newIndex].id : null;
      if (movedId) this.loadingIds.push(movedId);
      try {
        const data = new FormData();
        data.append('data', JSON.stringify(ids));
        await this.axios.post(`${this.$apiUrl}/v2/tv/channels/sequence`, data);
      } catch (err) {
        console.error('Failed to update sequence');
      }
      if (movedId) this.loadingIds = this.loadingIds.filter(function(id) { return id !== movedId; });
    },

    confirmDelete(channel) {
      this.ch = channel;
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Delete Channel',
        text: `Are you sure you want to delete "${channel.name}"?`,
        accept: this.deleteChannel
      });
    },

    async deleteChannel() {
      try {
        await this.axios.post(`${this.$apiUrl}/v2/tv/channels/${this.ch.id}/del`);
        this.$vs.notify({ color: 'success', title: 'Channel Deleted', text: '' });
        this.fetchData();
      } catch (err) {
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Failed to delete channel' });
      }
    },

    handleLogo() {
      const file = this.$refs.logo.files[0];
      if (file) {
        this.ch.logo = file;
        this.logoPreview = URL.createObjectURL(file);
      }
    },

    handleChImage() {
      const file = this.$refs.chImage.files[0];
      if (file) {
        this.ch.image = file;
        this.imagePreview = URL.createObjectURL(file);
      }
    },

    handleChSecImage() {
      const file = this.$refs.chSecImage.files[0];
      if (file) {
        this.ch.secondary_image = file;
        this.secImagePreview = URL.createObjectURL(file);
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.tv-channels-container {
  .view-toggle button {
    transition: all 0.2s;
  }

  .upload-box {
    border: 2px dashed #e2e8f0;
    border-radius: 0.5rem;
    height: 80px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    overflow: hidden;

    &:hover {
      border-color: #28c76f;
      background: #f0fff4;
    }

    &.small {
      height: 70px;
    }

    .upload-preview {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }

  .channel-card {
    position: relative;
    transition: all 0.2s;
    overflow: hidden;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

      .grid-drag-handle,
      .play-overlay {
        opacity: 1;
      }
    }

    .status-toggle {
      background: rgba(255, 255, 255, 0.9);
      padding: 4px;
      border-radius: 4px;
      cursor: pointer;
    }

    .badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      font-size: 9px;
      font-weight: bold;
      border-radius: 4px;

      &.featured {
        background: #fef3c7;
        color: #d97706;
      }

      &.kids {
        background: #dbeafe;
        color: #2563eb;
      }

      &.editor {
        background: #fce7f3;
        color: #db2777;
      }
    }
  }

  .drag-handle {
    cursor: grab;
    opacity: 0.5;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }

    &:active {
      cursor: grabbing;
    }
  }

  .drag-table {
    width: 100%;
    border-collapse: collapse;

    thead tr {
      background: #f8f8f8;
      border-bottom: 2px solid #e8e8e8;
    }

    th {
      padding: 10px 14px;
      text-align: left;
      font-size: 12px;
      font-weight: 600;
      color: #626262;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    tbody tr {
      border-bottom: 1px solid #f0f0f0;
      transition: background 0.15s;

      &:hover {
        background: #fafafa;
      }

      &.row-inactive {
        opacity: 0.5;
      }
    }

    td {
      padding: 10px 14px;
      vertical-align: middle;
    }

    // Ghost row: shows drop position indicator
    .drag-ghost {
      opacity: 0;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: 0;
        right: 0;
        height: 3px;
        background: #28c76f;
        border-radius: 2px;
        z-index: 10;
      }
    }

    // Chosen (picked up) row
    .drag-chosen {
      background: #f0fff4 !important;
      box-shadow: 0 4px 20px rgba(40, 199, 111, 0.2);
    }

    // Actively dragging row
    .drag-dragging {
      opacity: 0.9;
      background: #fff;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
      border-radius: 6px;
    }

    // Row selected
    .row-selected {
      background: #f0fff4 !important;
    }

    // Row with pending API call
    .row-loading {
      background: #f9fffe !important;
      animation: row-pulse 1s ease-in-out infinite;
    }
  }

  .bulk-checkbox {
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: #28c76f;
  }

  .bulk-action-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    margin-bottom: 10px;
    background: #f0fff4;
    border: 1px solid #28c76f;
    border-radius: 8px;

    .bulk-count {
      font-size: 13px;
      font-weight: 600;
      color: #28c76f;
      white-space: nowrap;
    }
  }

  .table-responsive {
    overflow-x: auto;
    position: relative;
  }

  .table-loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    border-radius: inherit;

    .loading-spinner {
      width: 36px;
      height: 36px;
      border: 3px solid #e0e0e0;
      border-top-color: #28c76f;
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @keyframes row-pulse {
    0%, 100% { background: #f9fffe; }
    50% { background: #e2fdf0; }
  }

  .inline-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #e0e0e0;
    border-top-color: #28c76f;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
  }
}

.channel-dialog,
.preview-dialog {
  .vs-popup {
    max-width: 650px;
    width: 95%;
  }
}
</style>
